import { Subject, takeUntil } from 'rxjs';
import writeXlsxFile from 'write-excel-file/browser';
import { AtsappService } from '@common/services/atsapp.service';
import { DateTransformService } from 'projects/speaker-platform/src/app/services/helpers/date-transform.service';
import { FiltersProps } from 'projects/speaker-platform/src/app/types/filters';
import { PriseVoix } from 'projects/speaker-platform/src/app/types/prisesVoix';
import { Speaker } from 'projects/speaker-platform/src/app/types/speaker';
import { ChoixCod } from 'projects/speaker-platform/src/app/types/tablettes';
import { CcLibelleService } from '../../services/helpers/cc-libelle-service.service';

export abstract class AbstractTextComponent {
  constructor(
    protected atsappService: AtsappService,
    private dateTransformService: DateTransformService,
    protected ccLibelleService: CcLibelleService
  ) {}

  phasesVocalesList: ChoixCod[] = [];
  typesMessageList: ChoixCod[] = [];
  languesList: ChoixCod[] = [];
  standbyTypes: ChoixCod[] = [];
  categoryPV: ChoixCod[] = [];
  speakersList: Speaker[] = [];
  textsToPatchIdList: string[] = [];
  textsToPatchList: any[] = [];
  isLoading: boolean = false;

  countNbMotToSend: number = 0;

  successSound: HTMLAudioElement = new Audio('assets/success.mp3');
  errorSound: HTMLAudioElement = new Audio('assets/error.mp3');

  destroy$: Subject<boolean> = new Subject<boolean>();

  playSuccessSound() {
    this.successSound.play();
  }

  playErrorSound() {
    this.errorSound.play();
  }

  /**
   * Compte le nombre de mots de manière plus fiable
   * @param text Le texte à analyser
   * @returns Le nombre de mots
   */
  countWords(text: string | null | undefined): number {
    if (!text || typeof text !== 'string') {
      return 0;
    }

    // Supprime les espaces en début/fin
    const cleanText = text.trim();

    if (cleanText === '') {
      return 0;
    }

    // Utilise une regex qui définit ce qu'est un "mot" :
    // \b\w+ : frontière de mot + un ou plusieurs caractères de mot (lettres, chiffres, _)
    // Cela gère automatiquement la ponctuation, espaces multiples, etc.
    const words = cleanText.match(/\b\w+\b/g);

    return words ? words.length : 0;
  }

  getPhasesVocale(): void {
    this.atsappService
      .getChoixCod('PHV')
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.phasesVocalesList = res;
      });
  }

  getCategoryPV(): void {
    this.atsappService
      .getChoixCod('ZCG')
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.categoryPV = res;
      });
  }

  getTypesMessage(): void {
    this.atsappService
      .getChoixCod('TMS')
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.typesMessageList = res;
      });
  }

  getLangues(): void {
    this.atsappService
      .getChoixCod('LGU')
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        // Français / Anglais / Allemand / Italien / Espagnol en premier dans la liste
        const firsts = ['FRF', 'ANG', 'ALL', 'ITA', 'ESP'];

        firsts.forEach((code: string) => {
          const index = res.findIndex((cc: ChoixCod) => cc.cc_code === code);
          this.languesList.push(res[index]);
        });

        // Ajouter le reste des langues non listées
        this.languesList.push(
          ...res.filter((o: ChoixCod) => !firsts.includes(o.cc_code))
        );
      });
  }

  getSpeakers() {
    this.atsappService
      .getSpeakers(false)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.speakersList = res;
      });
  }

  getTranslator() {
    this.atsappService
      .getSpeakers(false)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.speakersList = res.filter((spk: Speaker) =>  spk.traductrice);
      });
  }

  getStandbyTypes() {
    this.atsappService.getChoixCod('ZBY').subscribe({
      next: (res: any) => {
        this.standbyTypes = res.filter(
          (zby: ChoixCod) => zby.cc_abrege === 'PRISEVOIX'
        );
      },
    });
  }

  itemShouldBeChecked(id: string): boolean {
    return this.textsToPatchIdList.indexOf(id) > -1 ? true : false;
  }

  getCodes(ccLibelle: string, ccList: ChoixCod[]): string {
    return this.ccLibelleService.getCcCode(ccLibelle, ccList);
  }

  getLibelles(ccCode: string, ccList: ChoixCod[]) {
    return this.ccLibelleService.getCcLibelle(ccCode, ccList);
  }

  addToSendList(
    checked: boolean,
    value: string,
    smartSelectEnabled: boolean,
    prisesVoix: any,
    keys: string[] = []
  ): void {
    if (smartSelectEnabled) {
      if (checked) {
        const data = value.split('-');
        const textsToAdd = prisesVoix.filter(
          (pv: any) => pv.ZMS_NUMERO === data[0] && pv.CLIENT_FINAL === data[4]
        );
        textsToAdd.forEach((pv: any) => {
          const value =
            pv.ZMS_NUMERO +
            '-' +
            pv.ZMS_LIVRAISON +
            '-' +
            pv.ZMS_MESSAGE +
            '-' +
            pv.RANGPV +
            '-' +
            pv.CLIENT_FINAL;
          if (!this.textsToPatchIdList.includes(value)) {
            this.textsToPatchIdList.push(value);
          }
          this.buildPostBody(value, true, prisesVoix, keys);
        });
      } else {
        const index = this.textsToPatchIdList.indexOf(value);
        if (index > -1) {
          this.textsToPatchIdList.splice(index, 1);
          this.buildPostBody(value, false, prisesVoix, keys);
        }
      }

      return;
    }

    if (checked) {
      if (!this.textsToPatchIdList.includes(value)) {
        this.textsToPatchIdList.push(value);
      }
      this.buildPostBody(value, true, prisesVoix, keys);
    } else {
      const index = this.textsToPatchIdList.indexOf(value);
      if (index > -1) {
        this.textsToPatchIdList.splice(index, 1);
        this.buildPostBody(value, false, prisesVoix, keys);
      }
    }
  }

  buildPostBody(
    value: string,
    shouldAdd: boolean,
    prisesVoix: any,
    keys: string[]
  ): void {
    let searchParams = value.split('-');
    prisesVoix.filter((pv: any) => {
      if (
        pv.ZMS_NUMERO === searchParams[0] &&
        pv.ZMS_LIVRAISON === searchParams[1] &&
        pv.ZMS_MESSAGE === searchParams[2] &&
        pv.RANGPV === searchParams[3]
      ) {
        if (shouldAdd) {
          this.countNbMotToSend += this.countWords(pv.ZMS_PVTEXTE)
          const objectToPush = {};
          keys.forEach((key: string) => {
            Object.defineProperties(objectToPush, {
              [key]: {
                value: pv[key],
                enumerable: true,
                writable: true,
              },
            });
          });
          this.textsToPatchList.push(objectToPush);
        } else {
          this.countNbMotToSend -= this.countWords(pv.ZMS_PVTEXTE)
          const index: number = this.textsToPatchList.findIndex((pv: any) => {
            return (
              pv.ZMS_NUMERO === searchParams[0] &&
              pv.ZMS_LIVRAISON === searchParams[1] &&
              pv.ZMS_MESSAGE === searchParams[2] &&
              pv.RANGPV === searchParams[3]
            );
          });
          this.textsToPatchList.splice(index, 1);
        }
      }
    });
  }

  selectAllTexts(checked: boolean, prisesVoix: any[], keys: string[]): void {
    this.resetTextsToPatchList()
    prisesVoix.forEach((pv: any) => {
      this.addToSendList(
        checked,
        pv.ZMS_NUMERO +
          '-' +
          pv.ZMS_LIVRAISON +
          '-' +
          pv.ZMS_MESSAGE +
          '-' +
          pv.RANGPV +
          '-' +
          pv.CLIENT_FINAL,
        false,
        prisesVoix,
        keys
      );
    });
  }

  // updateFilters(
  //   payload: { filter: string; value: any },
  //   filters: FiltersProps[]
  // ) {
  //   for (const f of filters) {
  //     if (payload.filter === f.filter) {
  //       f.value =
  //         payload.filter === 'ZMS_DATELIVRAISON'
  //           ? this.dateTransformService.transformDate(payload.value)
  //           : payload.value;
  //     }
  //   }
  //   this.resetTextsToPatchList();
  // }

  // resetFilters(filters: FiltersProps[], callback: any) {
  //   filters.forEach((filter: FiltersProps) => {
  //     filter.value = '';
  //   });

  //   this.resetTextsToPatchList();
  //   callback;
  // }

  resetTextsToPatchList() {
    this.textsToPatchIdList = [];
    this.textsToPatchList = [];
    this.countNbMotToSend = 0;
  }

  colorFromTiers(tiers: number) {
    tiers >>>= 0;
    const b = tiers & 0xff,
      g = (tiers & 0xff00) >>> 8,
      r = (tiers & 0xff0000) >>> 16,
      a = ((tiers & 0xff000000) >>> 24) / 255;
    return 'rgba(' + [r, g, b, a].join(',') + ')';
  }

  async createExcelExportData(dataToExport: PriseVoix[]): Promise<void> {
    if (!dataToExport || dataToExport.length === 0) {
      return;
    }

    const columns = [
      {
        header: { value: 'Trigramme', fontWeight: 'bold' as const },
        cell: (pv: PriseVoix) => ({ value: `${pv.ZMS_NUMERO}-${pv.ZMS_LIVRAISON}-${pv.ZMS_MESSAGE}` }),
        width: 22,
      },
      {
        header: { value: 'Client final', fontWeight: 'bold' as const },
        cell: (pv: PriseVoix) => ({ value: pv.CLIENT_FINAL }),
        width: 14,
      },
      {
        header: { value: 'Raison sociale', fontWeight: 'bold' as const },
        cell: (pv: PriseVoix) => ({ value: pv.LIBELLE_CLIENT_FINAL }),
        width: 28,
      },
      {
        header: { value: 'Guide', fontWeight: 'bold' as const },
        cell: (pv: PriseVoix) => ({ value: pv.ZMS_GUIDE }),
        width: 25,
      },
      {
        header: { value: 'Phase vocale', fontWeight: 'bold' as const },
        cell: (pv: PriseVoix) => ({ value: this.getLibelles(pv.ZMS_PHASEVOCALE, this.phasesVocalesList) }),
        width: 18,
      },
      {
        header: { value: 'Date de livraison', fontWeight: 'bold' as const },
        // Valeur brute renvoyée par l'API (chaîne), exportée sans conversion
        cell: (pv: PriseVoix) => ({ value: pv.ZMS_DATELIVRAISON as unknown as string }),
        width: 16,
      },
      {
        header: { value: 'Speak', fontWeight: 'bold' as const },
        cell: (pv: PriseVoix) => ({ value: `${pv.PRENOM_COMEDIENNE} ${pv.NOM_COMEDIENNE}` }),
        width: 22,
      },
      {
        header: { value: 'Langue', fontWeight: 'bold' as const },
        cell: (pv: PriseVoix) => ({ value: pv.LANGUE }),
        width: 12,
      },
      {
        header: { value: 'Genre', fontWeight: 'bold' as const },
        cell: (pv: PriseVoix) => ({ value: pv.GENRE }),
        width: 10,
      },
      {
        header: { value: 'Position', fontWeight: 'bold' as const },
        cell: (pv: PriseVoix) => ({ value: Number(pv.RANGPV), type: Number }),
        width: 10,
      },
    ];

    const fileName = `prises-de-voix_${new Date().toISOString().slice(0, 10)}.xlsx`;
    await writeXlsxFile(dataToExport, { columns, sheet: 'Prises de voix' }).toFile(fileName);
  }
}
